import {
  ActionIcon, Avatar,
  Badge,
  Box,
  Card,
  Container,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Space,
  Tabs,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { openConfirmModal } from '@mantine/modals';
import pluralize from 'pluralize';
import { useDisclosure, useFocusTrap } from '@mantine/hooks';
import classes from './EventMapping.Mobile.module.css';
import { getMarketplaceLogo } from '../img/marketPlaceImage';
import { NavBarMobile } from '../hoc/NavBar/NavBar.Mobile';
import SyncAltIcon from '../components/icons/SyncAlt';
import { MarketplaceSelector } from '../hoc/SeatingChart/SeatingChart.MarketplaceSelector';
import { formatDate } from '../utils/formatters';
import { DateFormats } from '../utils/globals';
import DateRangeIcon from '../components/icons/DateRange';
import { BNTextInput } from '../components/TextInput/TextInput';
import SearchIcon from '../components/icons/Search';
import { BNButton } from '../components/Button/Button';
import { EventMappingSearchResults } from '../hoc/EventMapping/EventMapping.SearchResults';
import LinkIcon from '../components/icons/Link';
import { EventMappingSearchResultsCard } from '../hoc/EventMapping/EventMapping.SearchResultsCard';
import { EventMappingProvider, useEventMapping } from '../hoc/EventMapping/EventMapping.hooks';
import mobileClasses from './Mobile.module.css';
import ChevronBackIcon from '../components/icons/ChevronBack';
import MoreHorizIcon from '../components/icons/MoreHoriz';
import { useNavigate } from 'react-router-dom';
import ChevronForwardIcon from '../components/icons/ChevronForward';
import LiveHelpIcon from '../components/icons/LiveHelp';
import { InputModal } from '../hoc/Mobile/InputModal';
import { useGlobalState } from '../data/Global.state';
import { useTenantTheme } from '../components/hooks/useTenantTheme';

export function EventMappingMobile() {
  return (
    <EventMappingProvider>
      <EventMappingInner />
    </EventMappingProvider>
  );
}

function EventMappingInner() {
  const {
    setShowEventMapping,
    selectedEvent,
    searchForm,
    searchEvents,
    searchResults,
    selectedMarketplaceEvent,
    pasteUrlForm,
    pastedEvent,
    fetchPastedEvent,
    isPastedEventLoading,
    unlinkEvent,
    isUnlinkingEvent,
    linkEvent,
    clearSearchResults,
  } = useEventMapping(
    'setShowEventMapping',
    'selectedEvent',
    'searchForm',
    'searchEvents',
    'searchResults',
    'selectedMarketplaceEvent',
    'pasteUrlForm',
    'pastedEvent',
    'fetchPastedEvent',
    'isPastedEventLoading',
    'unlinkEvent',
    'isUnlinkingEvent',
    'linkEvent',
    'clearSearchResults',
  );
  const [menuModalOpened, { open: openMenuModal, close: closeMenuModal }] = useDisclosure(false);
  const [marketplaceModalOpened, { open: openMarketplaceModal, close: closeMarketplaceModal }] = useDisclosure(false);
  const focusTrapRef = useFocusTrap();
  const marketplaceLogo = getMarketplaceLogo(selectedMarketplaceEvent?.marketplaceId);
  const navigate = useNavigate();
  const { tenants, principal } = useGlobalState('tenants', 'principal');
  const isMultiTenant = tenants && tenants.length > 1;
  const tenantColorName = useTenantTheme(selectedEvent, isMultiTenant, principal);
  const tenantClassName = isMultiTenant ? `colors-tenant-mobile-${tenantColorName}` : 'tenant-single';

  return (
    <>
    {selectedEvent && (
      <Paper className={classes.eventMappingPanel}>
        <NavBarMobile
          leftSide={
            <ActionIcon
              opacity={0.7}
              size={36}
              onClick={() => {
                navigate(-1);
              }}
              className={mobileClasses.chevronIconButton}
            >
              <ChevronBackIcon size={20} color="white" />
            </ActionIcon>
          }
          rightSide={
            <ActionIcon opacity={0.7} size={36} onClick={() => openMenuModal()}>
              <MoreHorizIcon size={24} color="white" />
            </ActionIcon>
          }
          titleArea={
            <Text fw={600} ta="center" c="white" lh={1} fz={14} truncate>
              Edit Event Mapping
            </Text>
          }
          containerStyle={{ background: `var(--${tenantClassName}, var(--colors-mobileBg))` }}
          headerBg="transparent"
        />
        <Group p={0} pt={12} pb={0} wrap="wrap" justify="space-between" style={{ height: 'min-content' }}>
               {/* Event POS Data Card */}
          <Card mx={0} p="md" py="sm" mt="sm" mb={16} className={classes.card}>
            <Group wrap="nowrap" justify="space-between" gap={8}>
              <Text c="var(--colors-brandcolor-7)" size="xs" fw={700} tt="uppercase">
                {formatDate(selectedEvent.localDateTime, DateFormats.Standard)}
              </Text>
              <Group>
              <ActionIcon>{typeof marketplaceLogo !== 'string' ? (
                <Avatar
                  key="seatgeek"
                  m={0}
                  radius="xl"
                  variant="outline"
                  className={classes.avatar}
                  color="var(--colors-darkPaper)"
                  onClick={() => {
                    openMarketplaceModal();
                  }}
                >
                  <ActionIcon>
                    <SyncAltIcon color="var(--colors-gray-5)" size={20} />
                  </ActionIcon>
                  {marketplaceLogo}
                </Avatar>
              ) : (
                <Avatar
                  key="other"
                  m={0}
                  radius="xl"
                  variant="outline"
                  className={classes.avatar}
                  color="var(--colors-darkPaper"
                  onClick={() => openMarketplaceModal()}
                >
                  <ActionIcon>
                    <SyncAltIcon color="var(--colors-gray-5)" size={20} />
                  </ActionIcon>
                  <img
                    className="mantine-Avatar-image"
                    height={40}
                    width={40}
                    src={marketplaceLogo}
                    alt="Selected Marketplace"
                  />
                </Avatar>
              )}
              </ActionIcon>
              <Badge variant="light" size="xs" color="var(--colors-gray-5)" bg="var(--colors-divider)">
                POS Data
              </Badge>
              </Group>
            </Group>
            <Group>
              <Box pr={20} style={{ flex: 1, overflow: 'hidden' }}>
                <Text size="sm" fw={600} mt={6} lh={1.3}>
                  {selectedEvent.name}
                </Text>
                <Group wrap="nowrap" w="100%" gap={0} mt={6}>
                  <Text size="xs" c="var(--colors-gray-5)" truncate>
                    {selectedEvent.venue.name}
                  </Text>
                  <Text size="xs" c="gray.5" style={{ whiteSpace: 'nowrap' }}>
                    &nbsp;·&nbsp;{selectedEvent.venue.city}, {selectedEvent.venue.state}
                  </Text>
                </Group>
              </Box>
            </Group>
          </Card>
        </Group>
        <Tabs defaultValue="search" className={classes.tabs}>
          <Tabs.List pl={16} pr={16} className={classes.tabList}>
            <Tabs.Tab value="search">Search</Tabs.Tab>
            <Tabs.Tab value="link">Paste URL</Tabs.Tab>
            <Tabs.Tab
              value="unlink"
              disabled={selectedMarketplaceEvent === undefined || selectedMarketplaceEvent?.marketplaceEventId === null || selectedMarketplaceEvent?.isUserMapped !== true}
            >
              Un-link
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="search" pl={16} pr={16} pt={20} pb={20} className={classes.tabPanel}>
            <Box className={classes.tabPanelContentWrapper}>
              <Group className={classes.searchFormRow}>
                <Box className={classes.flex1}>
                  <Group wrap="nowrap" pb={4} gap={12}>
                    <Box w={142}>
                      <DatePickerInput
                        type="range"
                        leftSection={<DateRangeIcon />}
                        valueFormat="MM/DD"
                        size="xs"
                        placeholder="Pick date range"
                        label=""
                        allowSingleDateInRange
                        firstDayOfWeek={0}
                        {...searchForm.getInputProps('dateRange')}
                      />
                    </Box>
                    <Box className={classes.fullFlex1} ref={focusTrapRef}>
                      <BNTextInput
                        leftSection={<SearchIcon />}
                        size="xs"
                        className={classes.flex1}
                        label=""
                        placeholder="Search Marketplace Events"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            searchEvents();
                          }
                        }}
                        {...searchForm.getInputProps('searchTerm')}
                        clearable
                        clearableOnClick={() => searchForm.setFieldValue('searchTerm', '')}
                        data-autofocus
                      />
                    </Box>
                  </Group>
                  <Group justify="space-between" align="center" mt={8}>
                    <Text size="xs" c="var(--colors-gray-5)">
                      {searchResults && searchResults.length > 0 && (
                        <span>
                          {searchResults.length} {pluralize('result', searchResults.length)} from the marketplace
                        </span>
                      )}
                    </Text>
                    <Group justify="right" align="center" gap={12}>
                      <BNButton
                        variant="default"
                        color="gray"
                        size="xs"
                        onClick={() => {
                          clearSearchResults();
                          searchForm.reset();
                        }}
                      >
                        Reset
                      </BNButton>
                      <BNButton variant="filled" color="gray" size="xs" onClick={() => searchForm.onSubmit(() => searchEvents())()}>
                        Search
                      </BNButton>
                    </Group>
                  </Group>
                </Box>
              </Group>
              <EventMappingSearchResults />
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="link" pl={16} pr={16} pt={20} pb={20} className={classes.tabPanel}>
            <Box className={classes.tabPanelContentWrapper}>
              <Group className={classes.searchFormRow} gap={0}>
                <Box className={classes.flex1} w="100%">
                  <BNTextInput
                    leftSection={<LinkIcon />}
                    size="xs"
                    className={classes.flex1}
                    label=""
                    placeholder="Paste an Event URL"
                    {...pasteUrlForm.getInputProps('url')}
                    clearable
                    clearableOnClick={() => pasteUrlForm.setFieldValue('url', '')}
                  />
                </Box>
              </Group>
              <Group justify="right">
                <BNButton variant="filled" color="gray" size="xs" onClick={() => pasteUrlForm.onSubmit(() => fetchPastedEvent())()}>
                  Submit
                </BNButton>
              </Group>
              <Container p={0} m={0} className={classes.resultsContainer}>
                <Box className={classes.resultsScroller}>
                  {!pastedEvent && !isPastedEventLoading && (
                    <Box p={20} className={classes.emptyState}>
                      <Text c="gray.5" size="xs">
                        Result will appear here
                      </Text>
                    </Box>
                  )}
                  {!pastedEvent && isPastedEventLoading && (
                    <Box className={classes.loadingState}>
                      <Loader color="var(--colors-gray-5)" type="dots" />
                    </Box>
                  )}
                  {pastedEvent && (
                    <EventMappingSearchResultsCard
                      event={pastedEvent}
                      selectedMarketplaceEventId={selectedMarketplaceEvent.marketplaceEventId!}
                      posEvent={selectedEvent}
                      marketplaceId={selectedMarketplaceEvent.marketplaceId!}
                      onLinkEvent={() => {
                        linkEvent(selectedMarketplaceEvent.marketplaceId!, selectedEvent.eventId, {
                          marketplaceId: selectedMarketplaceEvent.marketplaceId!,
                          marketplaceEventId: pastedEvent.eventId,
                          marketplaceEvent: pastedEvent,
                          posEvent: selectedEvent,
                        });
                        setShowEventMapping(false);
                      }}
                    />
                  )}
                </Box>
              </Container>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="unlink" pl={16} pr={16} pt={20} pb={20} className={classes.tabPanel}>
            <Box className={classes.tabPanelContentWrapper}>
              <Container p={0} m={0} className={classes.resultsContainer}>
                <Box className={classes.resultsScroller} mb="xs">
                  <EventMappingSearchResultsCard
                    marketplaceId={selectedMarketplaceEvent.marketplaceId!}
                    event={selectedMarketplaceEvent.marketplaceEvent!}
                    posEvent={selectedEvent}
                    selectedMarketplaceEventId={selectedMarketplaceEvent.marketplaceEventId!}
                  />
                </Box>
                <BNButton
                  variant="outline"
                  size="xs"
                  color="red"
                  fullWidth
                  loading={isUnlinkingEvent}
                  onClick={() => {
                    openConfirmModal({
                      title: 'Remove Event Mapping',
                      children: <Text size="sm">Are you sure you want to un-link this event?</Text>,
                      labels: { confirm: 'Confirm', cancel: 'Cancel' },
                      confirmProps: { className: 'confirmButton', variant: 'filled', color: 'gray', size: 'sm' },
                      cancelProps: { className: 'cancelButton', variant: 'default', size: 'sm' },
                      closeButtonProps: { size: 'md' },
                      onConfirm: () => {
                        unlinkEvent(selectedMarketplaceEvent.marketplaceId!, selectedEvent.eventId);
                      },
                    });
                  }}
                >
                  Un-Link This Event
                </BNButton>
              </Container>
            </Box>
          </Tabs.Panel>
        </Tabs>
      </Paper>)}
      {marketplaceModalOpened && (
        <InputModal closeModal={closeMarketplaceModal}>
          <Box p="xs">
            <Flex align="center" justify="space-between" p={4}>
              <Text fw={600} size="md">
                Marketplace
              </Text>
              <BNButton onClick={() => closeMarketplaceModal()} variant="subtle" c="var(--colors-green-7)" fz="md" size="compact-xs" px={0} py={0} h={28} w={48}>
                Done
              </BNButton>
            </Flex>
            <MarketplaceSelector onChange={() => {}} />
            <Space h="xl" w="100%" />
          </Box>
        </InputModal>
      )}
      {menuModalOpened && (
        <MoreMenuModal
          modalOpened
          closeModal={closeMenuModal}
          changeMarketplaceClick={() => {
            closeMenuModal();
            openMarketplaceModal();
          }}
        />
      )}
    </>);
}

function MoreMenuModal({ modalOpened, closeModal, changeMarketplaceClick }: {
  changeMarketplaceClick: () => void;
  modalOpened: boolean;
  closeModal: () => void;
}) {
  return (
    <Modal
      opened={modalOpened}
      onClose={() => closeModal()}
      closeButtonProps={{ size: 'md' }}
      withCloseButton
      className={classes.bottomSheetMenuModal}
      title="Marketplace Actions"
      transitionProps={{ transition: 'slide-up' }}
    >
      <Flex p={0} h="100%" justify="space-between" pb="xl">
        <Flex className={mobileClasses.mobileMenuItemsWrapper} direction="column" w="100%">
          <Menu>
            <Menu.Item
              onClick={changeMarketplaceClick}
              leftSection={<SyncAltIcon size={21} />}
              rightSection={<ChevronForwardIcon size={14} color="var(--colors-gray-5)" />}
              fw={400}
            >
              Change Marketplace
            </Menu.Item>
            <Menu.Item onClick={() => {}} leftSection={<LiveHelpIcon size={21} />} rightSection={<ChevronForwardIcon size={14} color="var(--colors-gray-5)" />} fw={400}>
              Request Support
            </Menu.Item>
          </Menu>
        </Flex>
      </Flex>
    </Modal>
  );
}
